<template>
  <div class="text-element-wrapper">
    <div class="element-header">
      <span class="element-type-badge">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
        <span>Message</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
          </svg>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
    
    <div class="message-content" @click.stop="handleMessageContentClick">
      <!-- Mode affichage du message -->
      <div class="message-display-container" v-if="!isEditingMessage" @click="startEditMessage">
        <div class="message-bubble">
          <p class="message-text" v-if="element.text || element.message" v-html="formatMessage(element.text || element.message)"></p>
          <p class="message-placeholder" v-else>Cliquez pour ajouter un message...</p>
          <div class="message-time">{{ formattedTime }}</div>
        </div>
        <span class="char-count" :class="{'char-count-limit': (element.text || element.message || '').length >= 640}">{{ (element.text || element.message || '').length }}/640</span>
      </div>
      
      <!-- Mode édition du message -->
      <div v-show="isEditingMessage" class="message-editor-container">
        <!-- Message Editor Background (mimics bubble) -->
        <div class="message-bubble message-bubble-editing">
          <!-- Emoji button inside the bubble to conserve the exact same bounds -->
          <div class="emoji-button-container">
            <button type="button" class="emoji-button" @mousedown.prevent="$event => toggleEmojiPicker($event)">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
            </button>
            
            <!-- Sélecteur d'emojis -->
            <div v-if="showEmojiPicker" class="emoji-picker" @mousedown.prevent>
              <div class="emoji-picker-content">
                <div class="emoji-category">
                  <button 
                    v-for="emoji in commonEmojis" 
                    :key="emoji" 
                    type="button"
                    class="emoji-btn" 
                    @mousedown.prevent="(event) => insertEmoji(emoji, event)"
                  >
                    {{ emoji }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="message-editor-wrapper">
            <textarea 
              ref="messageEditor"
              v-model="editedMessage" 
              class="message-input" 
              placeholder="Écrivez votre message ici..."
              @input="handleMessageInput"
              @keydown="handleMessageKeydown"
              @blur="handleBlur"
              maxlength="640"
            ></textarea>
            
            <!-- Suggestions de variables (positioned relative to the wrapper) -->
            <div v-if="showVariableSuggestions" class="variable-suggestions" @mousedown.prevent>
              <button 
                v-for="(variable, index) in filteredVariableSuggestions" 
                :key="index"
                type="button"
                class="variable-suggestion-item"
                @mousedown.prevent="(event) => insertVariable(variable, event)"
              >
                {{variable.id}}
              </button>
            </div>
          </div>
        </div>
        <span class="char-count" :class="{ 'char-count-limit': editedMessage.length >= 640 }">
          {{ editedMessage.length }}/640
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TextElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isEditingMessage: false,
      editedMessage: '',
      showEmojiPicker: false,
      showVariableSuggestions: false,
      variableSuggestionQuery: '',
      cursorPosition: 0,
      isEmojiInteraction: false,
      isInsertingEmoji: false,
      isClickInsideMessageContent: false,
      commonEmojis: ['😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '🙂', '🤗', '🤩', '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓', '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵', '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🥳', '🥴', '🥺', '🤠', '🤡', '🤥', '🤫', '🤭', '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '👍', '👎', '👌', '✌️', '🤞', '🤘', '🤙', '👈', '👉', '☝️', '👆', '👇', '✋', '🤚', '🖐️', '🖖', '👋', '🤟', '✍️', '👏', '👐', '🙌', '🤲', '🙏', '🤝', '💪', '💄', '💋', '👄', '👅', '👂', '👃', '👁️', '👀', '🧠', '👶', '👦', '👧', '👨', '👩', '👴', '👵', '🤱', '🤰', '👼', '🎅', '🤶', '🦸', '🦹', '🧙', '🧚', '🧛', '🧜', '🧝', '🧞', '🧟', '💁', '🙅', '🙆', '🙋', '🙇', '🤦', '🤷', '💆', '💇', '🚶', '🏃', '💃', '🕺', '🤺', '🏇', '⛷️', '🏂', '🏌️', '🏄', '🚣', '🏊', '⛹️', '🏋️', '🚴', '🚵', '🤸', '🤼', '🤽', '🤾', '🤹', '❤️', '💚', '💙', '💜', '🧡', '💛', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '☮️', '✝️', '☪️', '🕉️', '☸️', '✡️', '🔯', '🕎', '☯️', '☦️', '🛐', '⛎', '♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓']
    };
  },
  computed: {
    formattedTime() {
      return new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },
    filteredVariableSuggestions() {
      if (!this.variableSuggestionQuery) {
        return this.variables;
      }
      
      return this.variables.filter(variable => 
        variable.id.toLowerCase().includes(this.variableSuggestionQuery.toLowerCase())
      );
    }
  },
  created() {
    // Créer une version liée de closeEmojiPickerOnClickOutside pour préserver le contexte this
    this.boundCloseEmojiPicker = this.closeEmojiPickerOnClickOutside.bind(this);
    this.boundHandleGlobalClick = this.handleGlobalClick.bind(this);
  },
  beforeUnmount() {
    // Nettoyer les écouteurs d'événements lors de la destruction du composant
    document.removeEventListener('click', this.boundCloseEmojiPicker);
    document.removeEventListener('click', this.boundHandleGlobalClick);
  },
  methods: {
    startEditMessage() {
      this.isEditingMessage = true;
      this.editedMessage = this.element.text || this.element.message || '';
      
      // Ajouter un écouteur global pour les clics en dehors du conteneur de message
      document.addEventListener('click', this.boundHandleGlobalClick);
      
      this.$nextTick(() => {
        if (this.$refs.messageEditor) {
          this.$refs.messageEditor.focus();
          this.cursorPosition = this.editedMessage.length;
        }
      });
    },
    
    saveEditedMessage() {
      const updatedElement = { 
        ...this.element, 
      
        message: this.editedMessage 
      };
      this.$emit('update', updatedElement);
      
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Nettoyer les écouteurs d'événements
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    cancelEditMessage() {
      this.isEditingMessage = false;
      this.showEmojiPicker = false;
      this.showVariableSuggestions = false;
      
      // Nettoyer les écouteurs d'événements
      document.removeEventListener('click', this.boundHandleGlobalClick);
    },
    
    handleMessageInput(event) {
      // Mettre à jour la position du curseur
      this.cursorPosition = event.target.selectionStart;
      
      // Vérifier si l'utilisateur est en train de taper une variable
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const match = textBeforeCursor.match(/{{([^}]*)$/); // Cherche {{ suivi de caractères jusqu'au curseur
      
      if (match) {
        this.showVariableSuggestions = true;
        this.variableSuggestionQuery = match[1];
      } else {
        this.showVariableSuggestions = false;
      }
    },
    
    handleMessageKeydown(event) {
      // Gérer la touche Tab pour l'autocomplétion
      if (event.key === 'Tab' && this.showVariableSuggestions && this.filteredVariableSuggestions.length > 0) {
        event.preventDefault();
        this.insertVariable(this.filteredVariableSuggestions[0]);
      }
      
      // Si Shift + Enter est pressé, on sauvegarde le message
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        this.saveEditedMessage();
        return;
      }

      // Si Enter est pressé seul, on permet le retour à la ligne
      if (event.key === 'Enter' && !event.shiftKey) {
        return; // Laisse le comportement par défaut du textarea (retour à la ligne)
      }

      // Si Escape est pressé, on annule l'édition
      if (event.key === 'Escape') {
        event.preventDefault();
        this.cancelEditMessage();
      }
    },
    
    handleMessageContentClick() {
      // Marquer que le clic est à l'intérieur du conteneur de message
      this.isClickInsideMessageContent = true;
      
      // Réinitialiser le marqueur après un court délai
      setTimeout(() => {
        this.isClickInsideMessageContent = false;
      }, 100);
    },
    
    handleBlur(event) {
      // Ne pas traiter l'événement blur si nous sommes en train d'insérer un emoji
      if (this.isInsertingEmoji) {
        // Ne rien faire, l'insertion d'emoji gérera le focus
        return;
      }
      
      // Utiliser un délai pour permettre aux événements de clic de se produire d'abord
      setTimeout(() => {
        // Vérifier si le clic est sur un élément lié aux emojis ou aux variables
        const isEmojiRelated = document.activeElement && (
          document.activeElement.closest('.emoji-button') ||
          document.activeElement.closest('.emoji-picker') ||
          document.activeElement.closest('.emoji')
        );
        
        const isVariableRelated = document.activeElement && (
          document.activeElement.closest('.variable-suggestions') ||
          document.activeElement.closest('.variable-suggestion-item')
        );
        
        // Si le clic est à l'intérieur du conteneur de message ou sur un élément lié aux emojis/variables, ne pas sauvegarder
        if (this.isClickInsideMessageContent || isEmojiRelated || isVariableRelated) {
          // Redonner le focus au textarea
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
          return;
        }
        
        // Si le clic est en dehors du conteneur de message et pas sur un élément lié aux emojis/variables, sauvegarder le message
        this.saveEditedMessage();
      }, 100);
    },
    
    toggleEmojiPicker(event) {
      // Empêcher la propagation de l'événement pour éviter le blur du textarea
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      this.isEmojiInteraction = true;
      this.showEmojiPicker = !this.showEmojiPicker;
      this.showVariableSuggestions = false;
  
      if (this.showEmojiPicker) {
        // Ajouter un gestionnaire de clic global pour fermer le picker
        this.$nextTick(() => {
          // Utiliser setTimeout pour éviter que le clic actuel ne ferme immédiatement le picker
          setTimeout(() => {
            document.addEventListener('click', this.boundCloseEmojiPicker);
          }, 0);
        });
      } else {
        // Retirer le gestionnaire si on ferme le picker
        document.removeEventListener('click', this.boundCloseEmojiPicker);
      }
  
      // Redonner le focus au textarea après un court délai
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },
    
    closeEmojiPickerOnClickOutside(event) {
      // Vérifier si le sélecteur d'emojis est ouvert
      if (!this.showEmojiPicker) return;
      
      // Vérifier si le clic est sur le bouton emoji, sur le sélecteur d'emojis ou sur un emoji
      if (event.target.closest('.emoji-picker') || 
          event.target.closest('.emoji-button') || 
          event.target.closest('.emoji')) {
        return;
      }
      
      // Si on arrive ici, c'est qu'on a cliqué en dehors du picker et du bouton
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      // Redonner le focus au textarea après un court délai
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
          }
        }, 10);
      });
    },

    handleGlobalClick(event) {
      // Ne rien faire si nous ne sommes pas en mode édition
      if (!this.isEditingMessage) {
        document.removeEventListener('click', this.boundHandleGlobalClick);
        return;
      }
      
      // Vérifier si le clic est à l'intérieur du conteneur de message
      const isClickInsideMessageContent = event.target.closest('.message-content');
      const isClickOnEmojiOrVariable = event.target.closest('.emoji-picker') || 
                                      event.target.closest('.emoji-button') || 
                                      event.target.closest('.emoji') ||
                                      event.target.closest('.variable-suggestions') || 
                                      event.target.closest('.variable-suggestion-item');
      
      // Si le clic est en dehors du conteneur de message et pas sur un emoji ou une variable, sauvegarder le message
      if (!isClickInsideMessageContent && !isClickOnEmojiOrVariable && !this.isInsertingEmoji) {
        this.saveEditedMessage();
      }
    },
    
    insertEmoji(emoji, event) {
      // Empêcher tout comportement par défaut et propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // Indiquer que nous sommes en train d'insérer un emoji
      this.isInsertingEmoji = true;
      
      // Capturer la position actuelle du curseur au moment de l'insertion
      if (this.$refs.messageEditor) {
        this.cursorPosition = this.$refs.messageEditor.selectionStart;
      }
      
      // Insérer l'emoji à la position du curseur
      const start = this.editedMessage.substring(0, this.cursorPosition);
      const end = this.editedMessage.substring(this.cursorPosition);
      this.editedMessage = start + emoji + end;
      
      // Fermer le sélecteur d'emoji mais garder le mode édition actif
      this.showEmojiPicker = false;
      document.removeEventListener('click', this.boundCloseEmojiPicker);
      
      // Utiliser un setTimeout pour s'assurer que le focus est maintenu
      // et pour éviter que l'événement blur ne se déclenche avant que nous ayons fini
      this.$nextTick(() => {
        setTimeout(() => {
          // Réinitialiser le flag après un court délai
          this.isInsertingEmoji = false;
          
          // Mettre à jour la position du curseur après l'emoji
          if (this.$refs.messageEditor) {
            this.$refs.messageEditor.focus();
            const newPosition = this.cursorPosition + emoji.length;
            this.$refs.messageEditor.selectionStart = newPosition;
            this.$refs.messageEditor.selectionEnd = newPosition;
            this.cursorPosition = newPosition;
          }
        }, 100);
      });
    },
    handleVariableSelect(variableId) {
      if (!variableId) return;
      
      // Indiquer que nous sommes en train d'insérer (pour empêcher le blur)
      this.isInsertingEmoji = true; 
      
      const variableText = `{{${variableId}}}`;
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
      
      // Réinitialiser la sélection
      this.selectedVariableToInsert = null;
      
      // Mettre à jour la position du curseur
      this.$nextTick(() => {
        setTimeout(() => {
          this.isInsertingEmoji = false;
          if (this.$refs.messageEditor) {
            const newCursorPos = this.cursorPosition + variableText.length;
            this.$refs.messageEditor.focus();
            this.$refs.messageEditor.selectionStart = newCursorPos;
            this.$refs.messageEditor.selectionEnd = newCursorPos;
            this.cursorPosition = newCursorPos;
          }
        }, 100);
      });
    },
    
    insertVariable(variable, event) {
      // Empêcher tout comportement par défaut et propagation
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      
      // Indiquer que nous sommes en train d'insérer une variable
      this.isInsertingEmoji = true; // On utilise la même variable que pour les emojis
      const textBeforeCursor = this.editedMessage.substring(0, this.cursorPosition);
      const textAfterCursor = this.editedMessage.substring(this.cursorPosition);
      
      // Trouver où commence la variable en cours de saisie
      const match = textBeforeCursor.match(/{{([^}]*)$/); 
      
      if (match) {
        const startPos = textBeforeCursor.lastIndexOf('{{');
        const variableText = `{{${variable.id}}}`;
        
        // Remplacer le début de la variable par la variable complète
        this.editedMessage = textBeforeCursor.substring(0, startPos) + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur après la variable insérée
        this.$nextTick(() => {
          setTimeout(() => {
            // Réinitialiser le flag après un court délai
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = startPos + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
            }
          }, 100);
        });
      } else {
        // Si pas de variable en cours, insérer à la position du curseur
        const variableText = `{{${variable.id}}}`;
        this.editedMessage = textBeforeCursor + variableText + textAfterCursor;
        
        // Mettre à jour la position du curseur
        this.$nextTick(() => {
          setTimeout(() => {
            // Réinitialiser le flag après un court délai
            this.isInsertingEmoji = false;
            
            if (this.$refs.messageEditor) {
              const newCursorPos = this.cursorPosition + variableText.length;
              this.$refs.messageEditor.focus();
              this.$refs.messageEditor.selectionStart = newCursorPos;
              this.$refs.messageEditor.selectionEnd = newCursorPos;
              this.cursorPosition = newCursorPos;
            }
          }, 100);
        });
      }
      
      this.showVariableSuggestions = false;
    },
    formatMessage(text) {
      if (!text) return '';
      
      // Remplacer les sauts de ligne par des <br>
      let formattedText = text.replace(/\n/g, '<br>');
      
      // Mettre en évidence les variables {{variable}} avec un style distinct
      formattedText = formattedText.replace(/{{([^}]+)}}/g, '<span class="variable-highlight">{{$1}}</span>');
      
      return formattedText;
    }
  }
}
</script>

<style scoped>
.text-element-wrapper {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 10px 25px rgba(20, 48, 40, 0.08);
  border: 1px solid rgba(20, 48, 40, 0.1);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 20px;
}

.text-element-wrapper:hover {
  box-shadow: 0 15px 35px rgba(20, 48, 40, 0.12);
  border-color: rgba(218, 255, 209, 0.5);
  transform: translateY(-2px);
}

.element-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  background: #143028; /* Peelo Dark Green */
  border-bottom: 1px solid rgba(218, 255, 209, 0.1);
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.element-type-badge {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #daffd1; /* Peelo Mint */
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.element-type-badge svg {
  color: #daffd1;
}

.element-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(218, 255, 209, 0.15);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(218, 255, 209, 0.7);
  transition: all 0.25s ease;
}

.action-btn:hover {
  background: rgba(218, 255, 209, 0.1);
  color: #daffd1;
  border-color: #daffd1;
  transform: scale(1.05);
}

.delete-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #f87171;
  border-color: #ef4444;
}

.message-content {
  padding: 24px;
  cursor: text;
  min-height: 80px;
}

.message-display-container {
  position: relative;
  min-height: 60px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.message-display-container:hover {
  background: rgba(20, 48, 40, 0.02);
  border-radius: 12px;
}

.message-bubble {
  position: relative;
  background: #f8fafc;
  border-radius: 16px;
  padding: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  margin-bottom: 12px;
}

.message-text {
  margin: 0;
  padding: 0;
  color: #1a202c;
  font-size: 15px;
  line-height: 1.6;
  word-wrap: break-word;
  font-family: 'Inter', system-ui, sans-serif;
  font-weight: 400;
  white-space: pre-wrap;
}

.message-text .variable-highlight {
  background: #daffd1;
  color: #143028;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  border: 1px solid rgba(20, 48, 40, 0.1);
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 13px;
}

.message-placeholder {
  margin: 0;
  padding: 0;
  color: #94a3b8;
  font-size: 15px;
  font-style: italic;
}

.message-time {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  text-align: right;
  margin-top: 10px;
  text-transform: uppercase;
}

.char-count {
  position: absolute;
  right: 0;
  bottom: -4px;
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.char-count-limit {
  color: #ef4444;
  background: #fee2e2;
  border-color: #fca5a5;
  animation: pulse 1s infinite alternate;
}

@keyframes pulse {
  from { transform: scale(1); }
  to { transform: scale(1.05); }
}

/* Styles pour l'éditeur de message */
.message-editor-container {
  position: relative;
  margin: 0;
  padding: 0;
  min-height: 80px;
  animation: fadeIn 0.2s ease;
}

.message-bubble-editing {
  padding: 0 !important;
  background: #ffffff !important;
  border-color: #143028 !important;
  box-shadow: 0 0 0 3px rgba(218, 255, 209, 0.4) !important;
}

.message-editor-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.message-input {
  width: 100%;
  min-height: 80px;
  padding: 18px;
  padding-right: 50px;
  border: none;
  background: transparent;
  font-size: 15px;
  line-height: 1.6;
  resize: none;
  font-family: 'Inter', system-ui, sans-serif;
  color: #1a202c;
  white-space: pre-wrap;
  word-break: break-word;
  outline: none;
}


.emoji-button-container {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
}

.emoji-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.emoji-button:hover {
  background: #daffd1;
  color: #143028;
  border-color: #143028;
  transform: scale(1.1);
}

.emoji-picker {
  position: absolute;
  top: 100%;
  right: 0;
  width: 320px;
  max-height: 280px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  margin-top: 12px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  animation: slideIn 0.2s ease;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.emoji-picker-content {
  padding: 16px;
  max-height: 280px;
  overflow-y: auto;
}

.emoji-picker-content::-webkit-scrollbar {
  width: 6px;
}

.emoji-picker-content::-webkit-scrollbar-thumb {
  background: #143028;
  border-radius: 10px;
}

.emoji-category {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.emoji-btn {
  cursor: pointer;
  font-size: 20px;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
}

.emoji-btn:hover {
  background: #daffd1;
  transform: scale(1.2);
}

/* Variables suggestion styles */
.variable-suggestions {
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  width: 100% !important;
  max-height: 200px !important;
  background: #ffffff !important;
  border: 1.5px solid #143028 !important;
  border-top: none !important;
  border-radius: 0 0 16px 16px !important;
  box-shadow: 0 15px 30px rgba(20, 48, 40, 0.15) !important;
  z-index: 1000 !important;
  margin-top: -4px !important;
  overflow-y: auto;
  overflow-x: hidden;
}

.variable-suggestion-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 12px 18px;
  background: none;
  border: none;
  font-family: 'Inter', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #143028;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
}

.variable-suggestion-item:hover {
  background: #daffd1;
  color: #143028;
  padding-left: 24px;
}

.variable-suggestions::-webkit-scrollbar {
  width: 6px;
}

.variable-suggestions::-webkit-scrollbar-thumb {
  background: #143028;
  border-radius: 10px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Styles responsifs */
@media (max-width: 768px) {
  .text-element-wrapper {
    margin-bottom: 16px;
  }
  
  .element-header {
    padding: 10px 12px;
  }
  
  .message-content {
    padding: 12px;
  }
  
  .message-bubble {
    padding: 12px;
  }
  
  .emoji-picker {
    width: 250px;
    max-height: 200px;
  }
  
  .emoji-btn {
    min-width: 28px;
    min-height: 28px;
    font-size: 16px;
  }
}
</style>
